import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { SettlementApiService } from '../../services/settlement-api/SettlementApiService';
import { SettlementLine, SettlementPeriodSummary, SettlementPreview } from '../../types/Settlement';

interface ParticipantBalanceView {
  userId: string;
  username: string;
  salesReceived: number;
  reimbursementTotal: number;
  afterExpenses: number;
  profitShareAmount: number;
  targetIncome: number;
  netBalance: number;
}

@Component({
  selector: 'app-settlements-page',
  standalone: false,
  templateUrl: './settlements-page.component.html',
  styleUrl: './settlements-page.component.sass',
})
export class SettlementsPageComponent implements OnInit {
  public month = this.currentMonth();
  public preview: SettlementPreview | null = null;
  public periods: SettlementPeriodSummary[] = [];
  public loading = false;
  public closing = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public constructor(
    private settlementApi: SettlementApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await Promise.all([this.loadPreview(), this.loadPeriods()]);
  }

  public get revenueTotal(): number { return this.preview?.events.reduce((sum, event) => sum + event.revenue, 0) ?? 0; }
  public get costTotal(): number {
    return this.preview?.events.reduce((sum, event) => sum + event.purchaseTotal + event.directExpenseTotal + event.monthlyExpenseTotal, 0) ?? 0;
  }
  public get profitTotal(): number { return this.preview?.events.reduce((sum, event) => sum + event.netProfit, 0) ?? 0; }
  public get participantBalances(): ParticipantBalanceView[] {
    const balances = new Map<string, ParticipantBalanceView>();
    this.preview?.lines.forEach((line) => {
      const current = balances.get(line.userId) ?? {
        userId: line.userId,
        username: line.username,
        salesReceived: 0,
        reimbursementTotal: 0,
        afterExpenses: 0,
        profitShareAmount: 0,
        targetIncome: 0,
        netBalance: 0,
      };
      current.salesReceived += line.salesReceived;
      current.reimbursementTotal += this.getReimbursementTotal(line);
      current.afterExpenses += this.getAfterExpenses(line);
      current.profitShareAmount += line.profitShareAmount;
      current.targetIncome += this.getTargetIncome(line);
      current.netBalance += line.netBalance;
      balances.set(line.userId, current);
    });
    return [...balances.values()]
      .map((balance) => ({
        ...balance,
        salesReceived: this.round(balance.salesReceived),
        reimbursementTotal: this.round(balance.reimbursementTotal),
        afterExpenses: this.round(balance.afterExpenses),
        profitShareAmount: this.round(balance.profitShareAmount),
        targetIncome: this.round(balance.targetIncome),
        netBalance: this.round(balance.netBalance),
      }))
      .sort((left, right) => right.netBalance - left.netBalance);
  }

  public async onMonthChange(): Promise<void> {
    await this.loadPreview();
  }

  public async closePeriod(): Promise<void> {
    if (!this.preview?.isReadyToSettle) return;
    if (!window.confirm(`Зафиксировать взаиморасчеты за ${this.formatMonth(this.month)}? После закрытия цифры периода не меняются.`)) return;
    this.closing = true;
    this.errorMessage = null;
    try {
      const response = await this.settlementApi.closePeriod(this.month);
      const result = this.unwrap(response);
      if (result) {
        this.preview = result;
        this.successMessage = `Взаиморасчеты за ${this.formatMonth(this.month)} зафиксированы.`;
        this.notificationService.notify('finance', 'Закрыт период взаиморасчетов', this.formatMonth(this.month));
        await this.loadPeriods();
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.closing = false;
      this.changeDetector.detectChanges();
    }
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
  }

  public getReimbursementTotal(line: SettlementLine): number {
    return this.round(line.ticketPurchaseReimbursement + line.directExpenseReimbursement + line.monthlyExpenseReimbursement);
  }

  public getTargetIncome(line: SettlementLine): number {
    return this.round(this.getReimbursementTotal(line) + line.profitShareAmount);
  }

  public getAfterExpenses(line: SettlementLine): number {
    return this.round(line.salesReceived - this.getReimbursementTotal(line));
  }

  public getSettlementActionLabel(item: { netBalance: number }): string {
    if (item.netBalance > 0) return 'К переводу';
    if (item.netBalance < 0) return 'К получению';
    return 'Закрыто';
  }

  public getSettlementActionAmount(item: { netBalance: number }): number {
    return Math.abs(item.netBalance);
  }

  public formatMonth(value: string): string {
    const [year, month] = value.split('-').map(Number);
    return new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1));
  }

  public trackById(_index: number, item: { id?: string; eventId?: string; userId?: string }): string {
    return item.id ?? `${item.eventId ?? ''}-${item.userId ?? ''}`;
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private async loadPreview(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.settlementApi.getPreview(this.month);
      this.preview = this.unwrap(response);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadPeriods(): Promise<void> {
    try {
      const response = await this.settlementApi.getPeriods();
      const result = this.unwrap(response);
      if (result) this.periods = result;
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private unwrap<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }
    return response.response;
  }

  private currentMonth(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
